import { createEffect, createSignal, Show } from "solid-js";
import type { TodoStore } from "../lib/store";
import type { Priority } from "../lib/types";
import { Icon } from "./Icon";

interface AddTaskProps {
  store: TodoStore;
  inputRef: (element: HTMLInputElement) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
}

export function AddTask(props: AddTaskProps) {
  const [title, setTitle] = createSignal("");
  const [notes, setNotes] = createSignal("");
  const [dueDate, setDueDate] = createSignal("");
  const [priority, setPriority] = createSignal<Priority>("none");
  const [detailsOpen, setDetailsOpen] = createSignal(false);

  createEffect(() => {
    if (!props.open) setDetailsOpen(false);
  });

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    if (!title().trim()) return;
    await props.store.addTask({
      title: title(),
      notes: notes(),
      due_date: dueDate() || null,
      priority: priority(),
      project_id: props.store.projectFilter(),
    });
    setTitle("");
    setNotes("");
    setDueDate("");
    setPriority("none");
    setDetailsOpen(false);
    props.setOpen(false);
  }

  function cancel() {
    setTitle("");
    setNotes("");
    setDueDate("");
    setPriority("none");
    setDetailsOpen(false);
    props.setOpen(false);
  }

  return (
    <Show
      when={props.open}
      fallback={
        <button class="add-task-trigger" type="button" onClick={() => props.setOpen(true)}>
          <span class="add-task-plus"><Icon name="plus" size={16} /></span>
          <span>Add task</span>
        </button>
      }
    >
      <form class="add-task" onSubmit={submit}>
        <label class="sr-only" for="new-task">What do you need to do?</label>
        <input
          ref={props.inputRef}
          id="new-task"
          value={title()}
          onInput={(event) => setTitle(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Escape") cancel();
          }}
          placeholder="What do you need to do?"
          maxlength="180"
          autofocus
        />
        <Show when={detailsOpen()}>
          <label class="sr-only" for="new-task-notes">Task notes</label>
          <textarea
            id="new-task-notes"
            value={notes()}
            onInput={(event) => setNotes(event.currentTarget.value)}
            placeholder="Add a helpful note (optional)"
            maxlength="4000"
          />
        </Show>
        <div class="composer-footer">
          <div class="composer-options">
            <label class="compact-field">
              <span>Due</span>
              <input type="date" aria-label="Due date" value={dueDate()} onInput={(event) => setDueDate(event.currentTarget.value)} />
            </label>
            <label class="compact-field">
              <span>Priority</span>
              <select aria-label="Priority" value={priority()} onChange={(event) => setPriority(event.currentTarget.value as Priority)}>
                <option value="none">None</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </label>
            <button class="composer-detail-button" type="button" onClick={() => setDetailsOpen(!detailsOpen())}>
              {detailsOpen() ? "Hide note" : "Add note"}
            </button>
          </div>
          <div class="composer-actions">
            <button type="button" class="secondary-button" onClick={cancel}>Cancel</button>
            <button type="submit" class="primary-button" disabled={!title().trim()}>Add task</button>
          </div>
        </div>
      </form>
    </Show>
  );
}
